import { AppLayout } from "components/AppLayout/AppLayout";
import { QuickNavModal } from "components/QuickNavModal/QuickNavModal";
import { MAILROOM_QUICK_NAV_PAGES } from "constants/quickNav/mailroom";
import { ChatLeftNav } from "components/ChatLeftNav/ChatLeftNav";
import GroupSelect from "components/dashboard/sideBar/groupSelect";
import { DashboardLayout } from "components/DashboardLayout";
import { NoPermission } from "components/design/noPermission";
import { Header } from "components/DesignSystem/Header/Header";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Documents as DocumentsIcon } from "components/icons/LeftNav/Documents";
import { Home } from "components/icons/LeftNav/Home";
import { Intray } from "components/icons/LeftNav/Mailroom/Intray";
import { RegAgent } from "components/icons/LeftNav/Mailroom/RegAgent";
import { InkleMailroomLogo } from "components/icons/Logos/InkleMailroomLogo";
import { InkleMailroomSymbol } from "components/icons/Logos/InkleMailroomSymbol";
import { Mailbox } from "components/mailbox/mailbox";
import { MailRoomHomeCard } from "components/MaillRoomHomeCard/MailRoomHomeCard";
import { RAHomeCard } from "components/RAHomeCard/RAHomeCard";
import { RegisterAgent } from "components/RegisteredAgent/RegisterAgent";
import { RoutesAccrossApps } from "components/RoutesAcrossApps/RoutesAcrossApps";
import {
  LeftBar,
  LeftBarItem,
  LeftBarLogo,
  LeftBarSeparator,
} from "components/Sidebar/LeftBar";
import { SidebarStickyGroup } from "components/Sidebar/SidebarStickyGroup";
import { TodoLeftNav } from "components/TodoLeftNav/TodoLeftNav";
import { TopBar } from "components/TopBar/TopBar";
import { MAILROOM, REGISTERED_AGENT } from "constants/addons";
import {
  ACTION_ITEMS_CLICKED_FROM_SIDEBAR,
  CHAT_CLICKED_FROM_SIDEBAR,
  DOCUMENTS_CLICKED_FROM_SIDEBAR,
} from "constants/analyticsEvents";
import { FOREIGN_CA } from "constants/onBoarding";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useAuth } from "hooks/useAuth";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect } from "react";
import { Redirect, Switch, useLocation, useRouteMatch } from "react-router-dom";
import { RestrictedRoute } from "RestrictedRoute";
import { DashboardBanner } from "signInSignUp/DashboardBanner";
import "static/styles/containers/dashboard.css";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { MailroomActionItems } from "./MailroomForward/MailroomActionItems";
import { LockedRegisterAgent } from "components/RegisteredAgent/LockedRegisterAgent";
import { LockedMailroomComponent } from "components/mailbox/LockedMailComponent";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";

export const Navbar = () => {
  const { type } = useAuth();
  const { link } = useConstructInternalLink();
  const { trackEvent } = useAnalytics();

  return (
    <div className="t-h-full t-flex t-flex-col t-justify-between">
      <div>
        {type === FOREIGN_CA && (
          <div className="t-m-2">
            <GroupSelect />
          </div>
        )}
        <LeftBarItem exact to={link("")} icon={Home}>
          Home
        </LeftBarItem>

        <LeftBarItem to={link("/inbox")} icon={Intray}>
          Inbox
        </LeftBarItem>
        <LeftBarItem to={link("/registered-agent")} icon={RegAgent}>
          Registered Agent
        </LeftBarItem>
      </div>
    </div>
  );
};

export const MailroomDashboard = () => {
  const { isCustomer, isAdmin } = useRoleBasedView();
  const { first_name } = useAuth();

  const { path } = useRouteMatch();
  const { search } = useLocation();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const searchParams = new URLSearchParams(search);
  const companyFromUrl = searchParams.get("company");

  const { data: subscriptions, isSuccess } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId,
    },
    {
      skip: !entityId || !groupId,
    }
  );

  const isMailroomSubscribed = subscriptions
    ?.filter((s) => s.group_subscription)
    .some((subscription) => subscription.subscription_type === MAILROOM);

  const isRASubscribed = subscriptions
    ?.filter((s) => s.group_subscription)
    .some(
      (subscription) => subscription.subscription_type === REGISTERED_AGENT
    );

  const MailComponent = isCustomer ? Mailbox : NoPermission;

  const { dispatch: currentAppDispatch } = useCurrentAppContext();

  useEffect(() => {
    currentAppDispatch({
      type: "SET_CURRENT_APP",
      payload: { currentApp: "Mailroom" },
    });
  }, []);

  if (isAdmin && companyFromUrl) {
    const crmBase = "/admin/crm";
    return <Redirect to={`${crmBase}/${companyFromUrl}/mail`} />;
  }

  return (
    <AppLayout
      sidebar={
        <LeftBar
          useInternalLink
          logo={
            <LeftBarLogo
              logo={InkleMailroomLogo}
              symbol={InkleMailroomSymbol}
            />
          }
          showCollapse
          stickyNav={<SidebarStickyGroup />}
        >
          <Navbar />
        </LeftBar>
      }
      topbar={
        <TopBar
          logo={
            <LeftBarLogo
              logo={InkleMailroomLogo}
              symbol={InkleMailroomSymbol}
            />
          }
          nav={<Navbar />}
          stickyNav={<SidebarStickyGroup />}
        />
      }
      banner={<DashboardBanner />}
    >
      <QuickNavModal pages={MAILROOM_QUICK_NAV_PAGES} />
      <Switch>
        <RestrictedRoute exact path={`${path}/inbox/:mailId`}>
          <Redirect to={`${path}/inbox`} />
        </RestrictedRoute>
        <RestrictedRoute exact path={`${path}/action-items`}>
          <Redirect to={`${path}/tickets`} />
        </RestrictedRoute>
        <RestrictedRoute path={`${path}/inbox`}>
          <PermissionBasedView
            route="/mailroom/inbox"
            blockedUI={<LockedMailroomComponent />}
          >
            <DashboardLayout
              header={
                <Header
                  v2
                  title="Inbox"
                  right={
                    <div className="t-w-52">
                      <EntitySelector />
                    </div>
                  }
                />
              }
            >
              <MailComponent
                userGroupId={null}
                hasMailroomSubscription={null}
              />
            </DashboardLayout>
          </PermissionBasedView>
        </RestrictedRoute>

        <RestrictedRoute path={`${path}/registered-agent`}>
          <PermissionBasedView
            route="/mailroom/registered-agent"
            blockedUI={<LockedRegisterAgent />}
          >
            <RegisterAgent />
          </PermissionBasedView>
        </RestrictedRoute>

        <RestrictedRoute exact path={path}>
          <DashboardLayout
            header={
              <Header
                right={
                  <div className="t-w-52">
                    <EntitySelector />
                  </div>
                }
                v2
                title={`Welcome ${first_name}`}
              />
            }
          >
            <div className="t-flex t-flex-wrap t-gap-8 t-pt-6">
              <MailRoomHomeCard
                isMailroomSubscribed={
                  isSuccess ? Boolean(isMailroomSubscribed) : true
                }
              />
              <RAHomeCard
                isRASubscribed={isSuccess ? Boolean(isRASubscribed) : true}
              />
              {isMailroomSubscribed && <MailroomActionItems />}
            </div>
          </DashboardLayout>
        </RestrictedRoute>

        <RoutesAccrossApps />
      </Switch>
    </AppLayout>
  );
};
